import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";

import type { PersonMemory } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Pill } from "../../../ui/Pill";
import { SheetIcon } from "../../../ui/SheetIcon";
import { formatWhen } from "../shared/format";
import {
  CONCEPT_LEVELS,
  CONCEPT_LEVEL_WORDS,
  DETAIL_LEVEL_WORDS,
  EXPERIENCE_WORDS,
  ROLE_WORDS,
  humanise,
  moduleStatusWord,
  type ConceptLevel,
} from "../shared/words";
import {
  canChooseLevel,
  forgetPreference,
  forgetRemembered,
  raiseConcept,
  setDetailLevel,
  setExperience,
  setUsedInOtherDiscipline,
  type MemoryEdit,
} from "./memoryEdits";

interface MemoryPanelProps {
  memory: PersonMemory;
  /** The person can change it here (my profile). On the getting-started screen it is shown to read, with the two answers to give. */
  editable: boolean;
  disabled?: boolean;
  onEdit: (change: MemoryEdit) => void;
  /** Leaves out the history, the reminders and the list of concepts: the short form beside the tutor. */
  compact?: boolean;
}

function ChangedByAgent({ memory, field }: { memory: PersonMemory; field: string }) {
  const change = memory.changed_by_agent.find((entry) => entry.field === field);
  if (!change) return null;
  return (
    <Pill title={`Changed by the agent ${formatWhen(change.at)}`} className="ra-agent-mark">
      Changed by the agent
    </Pill>
  );
}

function Choice({
  label,
  value,
  options,
  disabled,
  onChange,
  extra,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  disabled?: boolean;
  onChange: (value: string) => void;
  extra?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="ra-choice">
      <label htmlFor={id} className="field__label">
        {label}
      </label>
      <select id={id} className="field__input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {extra}
    </div>
  );
}

function Forgettable({ title, items, empty, disabled, onForget }: { title: string; items: readonly string[]; empty: string; disabled?: boolean; onForget: (text: string) => void }) {
  return (
    <div>
      <h3>{title}</h3>
      {items.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <ul className="ra-forget">
          {items.map((text) => (
            <li key={text}>
              <span>{text}</span>
              <Button variant="quiet" disabled={disabled} onClick={() => onForget(text)} aria-label={`Remove: ${text}`}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * "What I'll remember about you" (ui-direction screens 1 and 7): the fixed schema of the person's memory, nothing
 * else. Only its owner sees it. The concept levels go up, never down; to start again the person clears the memory.
 */
export function MemoryPanel({ memory, editable, disabled, onEdit, compact = false }: MemoryPanelProps) {
  const concepts = Object.entries(memory.concepts).sort(([a], [b]) => a.localeCompare(b));
  const modules = Object.entries(memory.modules).sort(([a], [b]) => a.localeCompare(b));
  const history = memory.working_history;
  const last = history.last_session;

  return (
    <Card className="ra-section ra-memory" aria-labelledby="ra-memory-title">
      <h2 id="ra-memory-title">What I'll remember about you</h2>
      <p className="muted">
        Only you can see this. It is what the agent uses to explain only what you need. It holds no document content, no keys and nothing about other people.
      </p>

      <div className="ra-memory__grid">
        <p>
          <strong>{ROLE_WORDS[memory.role] ?? humanise(memory.role)}</strong>
          {memory.discipline ? <span className="muted"> in {memory.discipline}</span> : null}
        </p>

        {editable ? (
          <>
            <Choice
              label="How much explanation do you want?"
              value={memory.detail_level}
              disabled={disabled}
              options={(Object.keys(DETAIL_LEVEL_WORDS) as Array<keyof typeof DETAIL_LEVEL_WORDS>).map((level) => ({ value: level, label: DETAIL_LEVEL_WORDS[level] }))}
              onChange={(value) => onEdit(setDetailLevel(value as PersonMemory["detail_level"]))}
              extra={<ChangedByAgent memory={memory} field="detail_level" />}
            />
            <Choice
              label="Have you worked with ontologies, class libraries or DEXPI before?"
              value={memory.experience ?? ""}
              disabled={disabled}
              options={[
                { value: "", label: "Not answered", disabled: true },
                ...(Object.keys(EXPERIENCE_WORDS) as Array<keyof typeof EXPERIENCE_WORDS>).map((answer) => ({ value: answer, label: EXPERIENCE_WORDS[answer] })),
              ]}
              onChange={(value) => onEdit(setExperience(value as NonNullable<PersonMemory["experience"]>))}
              extra={<ChangedByAgent memory={memory} field="experience" />}
            />
            <Choice
              label="Have you used this app in another discipline?"
              value={memory.used_in_other_discipline === null || memory.used_in_other_discipline === undefined ? "" : memory.used_in_other_discipline ? "yes" : "no"}
              disabled={disabled}
              options={[
                { value: "", label: "Not answered", disabled: true },
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
              onChange={(value) => onEdit(setUsedInOtherDiscipline(value === "yes"))}
              extra={<ChangedByAgent memory={memory} field="used_in_other_discipline" />}
            />
          </>
        ) : (
          <dl className="ra-facts">
            <div className="ra-facts__row">
              <dt>Explanation</dt>
              <dd>{DETAIL_LEVEL_WORDS[memory.detail_level]}</dd>
            </div>
            <div className="ra-facts__row">
              <dt>Worked with ontologies before</dt>
              <dd>{memory.experience ? EXPERIENCE_WORDS[memory.experience] : "Not answered"}</dd>
            </div>
          </dl>
        )}
      </div>

      <section aria-labelledby="ra-memory-modules">
        <h3 id="ra-memory-modules">Getting started</h3>
        {modules.length === 0 ? (
          <p className="muted">No module is started yet.</p>
        ) : (
          <ul className="ra-statuses">
            {modules.map(([module, status]) => (
              <li key={module}>
                <strong>{module}</strong> <Pill>{moduleStatusWord(status)}</Pill>
              </li>
            ))}
          </ul>
        )}
        {compact ? <Link to="/profile">Open my profile to change it</Link> : <Link to="/onboarding">Open getting started</Link>}
      </section>

      <section aria-labelledby="ra-memory-concepts">
        <h3 id="ra-memory-concepts">Words and ideas</h3>
        <p className="muted">Not seen, explained, practised or shown. A level only goes up. To start again, clear your memory.</p>
        {concepts.length === 0 ? (
          <p className="muted">No word has been explained to you yet.</p>
        ) : (
          <ul className="ra-concepts">
            {concepts.map(([concept, level]) => (
              <li key={concept}>
                <span>{humanise(concept)}</span>
                {editable ? (
                  <select
                    aria-label={`Level of ${humanise(concept)}`}
                    className="field__input"
                    value={level}
                    disabled={disabled}
                    onChange={(event) => onEdit(raiseConcept(concept, event.target.value as ConceptLevel))}
                  >
                    {CONCEPT_LEVELS.map((choice) => (
                      <option key={choice} value={choice} disabled={!canChooseLevel(level, choice)}>
                        {CONCEPT_LEVEL_WORDS[choice]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Pill>{CONCEPT_LEVEL_WORDS[level]}</Pill>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {!compact && (
        <section aria-labelledby="ra-memory-history">
          <h3 id="ra-memory-history">Your work so far</h3>
          {history.items.length === 0 && history.deliverables.length === 0 && !last ? (
            <p className="muted">Nothing yet. Only names and kinds are kept, never the content of a document.</p>
          ) : (
            <dl className="ra-facts">
              {history.items.length > 0 && (
                <div className="ra-facts__row">
                  <dt>Items you worked on</dt>
                  <dd>{history.items.join(", ")}</dd>
                </div>
              )}
              {history.deliverables.length > 0 && (
                <div className="ra-facts__row">
                  <dt>Documents you used</dt>
                  <dd>
                    {history.deliverables.map((deliverable, index) => (
                      <span key={`${deliverable.name}-${index}`} className="ra-docname">
                        {deliverable.kind && <SheetIcon kind={deliverable.kind} size={14} />} {deliverable.name}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {last && (
                <div className="ra-facts__row">
                  <dt>Where you stopped</dt>
                  <dd>
                    {[last.item, last.step ? `step ${humanise(last.step).toLowerCase()}` : null, last.open_cards > 0 ? `${last.open_cards} open ${last.open_cards === 1 ? "card" : "cards"}` : null]
                      .filter(Boolean)
                      .join(", ") || "Not recorded"}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </section>
      )}

      {!compact && (
        <>
          <Forgettable
            title="Preferences"
            items={memory.preferences}
            empty="No preference yet. Tell the agent, for example, that you like smaller batches."
            disabled={disabled || !editable}
            onForget={(text) => onEdit(forgetPreference(text))}
          />
          <Forgettable
            title="Things you asked me to remember"
            items={memory.remember}
            empty={'Nothing yet. Say "remember ..." to the agent.'}
            disabled={disabled || !editable}
            onForget={(text) => onEdit(forgetRemembered(text))}
          />
        </>
      )}

      {memory.changed_by_agent.length > 0 && (
        <section aria-labelledby="ra-memory-agent">
          <h3 id="ra-memory-agent">Changed by the agent</h3>
          <ul className="ra-agent-changes">
            {memory.changed_by_agent.map((change, index) => (
              <li key={`${change.field}-${index}`}>
                {humanise(change.field)} <span className="muted">({formatWhen(change.at)})</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}
